import { forwardRef } from 'react';

import cs from 'classnames';

import { cn } from 'lib/helpers/utility';

import Icon from '../Icon/Icon';

import './lib/Checkbox.scss';

/**
 * @type {import('react').ForwardRefExoticComponent<import('./lib/propTypes').CheckboxProps & import('react').RefAttributes<HTMLElement>>}
 */

const Checkbox = forwardRef(
  (
    {
      id,
      label,
      labelTextColor,
      className,
      inputClassName,
      labelClassName,
      parentLabelClassName,
      innerLabelClassName,
      dropDownClassName,
      onChange,
      handleClick, // recommended to use onChange instead of handleClick
      isChecked = false,
      isPartial,
      isDropdown,
      children,
      expanded,
      setExpanded,
      forwardedRef,
      disabled,
      color,
      radio = false,
      size = 16, // The dynamic size prop with a default value
      icon = null,
      iconColor = null,
      iconClassName = null,
      ...rest
    },
    ref
  ) => {
    return (
      <div
        className={cs(
          'Checkbox',
          className,
          isChecked && 'Checkbox--checked',
          children && 'Checkbox--hasChildren',
          expanded && 'Checkbox--expanded'
        )}
        style={{ '--checkbox-size': `${size}px`, '--width-multiplier': size <= 18 ? 0.16 : 0.22 }}>
        {isDropdown ? (
          <div className={cs('Checkbox__isDropdown', dropDownClassName)}>
            <div
              onClick={setExpanded}
              className={cs(
                inputClassName,
                'flex w-full cursor-pointer items-center px-4 py-[11px]',
                disabled ? '!cursor-not-allowed' : 'cursor-pointer'
              )}>
              <div className={cn('flex items-center gap-2', innerLabelClassName)}>
                {icon && (
                  <Icon
                    icon={icon}
                    color={iconColor}
                    className={cs(disabled && 'cursor-not-allowed', iconClassName)}
                  />
                )}

                <input
                  style={{ width: size, height: size }} // Apply dynamic size here
                  className={cs(
                    inputClassName,
                    isChecked && isPartial ? 'isPartial' : '',
                    disabled ? '!cursor-not-allowed' : 'cursor-pointer',
                    inputClassName
                  )}
                  data-qa={`checkbox-${id}`}
                  id={id}
                  type="checkbox"
                  checked={isChecked}
                  onChange={onChange || handleClick}
                  ref={forwardedRef}
                  value={isChecked}
                  disabled={disabled}
                  {...rest}
                />
                <span
                  className={cs('w-fit', color === 'blue' && 'text-primary-900', labelTextColor)}
                  ref={ref}>
                  {label}
                </span>
              </div>
              <Icon
                icon="chevron-down-filled"
                className={cs(
                  'arrow-down',
                  expanded && 'rotate-180',
                  disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                )}
              />
            </div>
            {children && <div className="Checkbox__children">{children}</div>}
          </div>
        ) : (
          <label htmlFor={id} className={labelClassName}>
            <div className={cs('flex w-full cursor-pointer items-center', parentLabelClassName)}>
              <div className={cn('flex items-center gap-2', innerLabelClassName)}>
                {icon && (
                  <Icon
                    icon={icon}
                    color={iconColor}
                    className={cs(disabled && 'cursor-not-allowed', iconClassName)}
                  />
                )}

                <input
                  style={{ width: size, height: size }} // Apply dynamic size here
                  className={cs(
                    color === 'blue' && 'Checkbox--blue',
                    radio && 'Checkbox--radio',
                    disabled ? '!cursor-not-allowed' : 'cursor-pointer',
                    inputClassName,
                    isChecked && isPartial ? 'isPartial' : ''
                  )}
                  data-qa={`checkbox-${id}`}
                  id={id}
                  type="checkbox"
                  readOnly={!onChange || !handleClick}
                  checked={isChecked}
                  onChange={onChange || handleClick}
                  ref={forwardedRef}
                  value={isChecked}
                  disabled={disabled}
                  {...rest}
                />
                {label && (
                  <span
                    className={cs('w-fit', color === 'blue' && 'text-primary-900', labelTextColor)}
                    ref={ref}>
                    {label}
                  </span>
                )}
              </div>
            </div>
            {children && <div className="Checkbox__children">{children}</div>}
          </label>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
